import React, { useEffect, useState } from 'react';
import { Box, Icon, TableSortable } from '@saviynt/design-system';
import { format } from 'sql-formatter';

import { useChatOverlayContext } from '../../context/chatOverlayContext';
import {
  type AskApiResponse,
  type HighConfidenceTableAskApiResponse,
  type SampleDataAPIResponse,
  type SampleDataSuccessResponse,
} from '../../data-layer/types';
import { useSaviChatAskApi } from '../../hooks';
import { Logger } from '../../services';
import { isValidUrl } from '../../services/ispmUtil';
import { CHATOVERLAY_DATAPANEL } from '../../tests/testids';
import { useInvertedLocalize } from '../../utils/localization';
import { invertedMessages } from '../../utils/messages';
import Iframe from '../Iframe/Iframe';

import ContentNotAccurate from './ContentNotAccurate/ContentNotAccurate';
import GeneratingContent from './GeneratingContent/GeneratingContent';
import LowConfidenceHeader from './panel-headers/LowConfidenceHeader/LowConfidenceHeader';
import TableHeader from './panel-headers/TableHeader/TableHeader';
import TopHeaderPanel from './panel-headers/TopHeaderPanel/TopHeaderPanel';
import SqlErrorToggle from './sql-error-panel/toggle-section/SqlErrorToggle';
import CantBeDisplayedSqlError from './sql-error-panel/visualization-section/CantBeDisplayedSqlError';
import {
  type GeneratedContentType,
  GeneratedContentTypeEnum,
  TOGGLE_VALUES,
  type ToggleSelectionType,
} from './types';

import './GeneratedContentContainer.css';

interface GeneratedContentContainerProps {
  kind: GeneratedContentType;
  data: AskApiResponse | SampleDataAPIResponse;
}

function GeneratedContentContainer({
  kind,
  data,
}: GeneratedContentContainerProps): JSX.Element {
  const { updateChatOverlayState } = useChatOverlayContext();
  // TODO (ISPM): header font size is different (smaller) for tables

  const [toggleSelection, setToggleSelection] = useState<ToggleSelectionType>(
    TOGGLE_VALUES.VISUALIZATION
  );
  const { handleLowConfidenceSampleDataApi } = useSaviChatAskApi();
  const l = useInvertedLocalize(invertedMessages);
  const MAX_TABLE_ROWS_TO_SHOW = 100;

  useEffect(() => {
    // Check is its low confidence ask api response
    if (
      'contentType' in data &&
      data.contentType === 'query' &&
      !('error' in data)
    ) {
      setToggleSelection(TOGGLE_VALUES.SQL);
    }
  }, [data]);

  const generateTableContent = (
    tableData: HighConfidenceTableAskApiResponse | SampleDataSuccessResponse
  ) => {
    if (!Array.isArray(tableData.content) || !tableData.content.length) {
      return null;
    }

    const cols = tableData.content[0]; // first row is the header
    const columns = Object.keys(cols).map((key) => ({
      key,
      label: cols[key],
      isSortable: true,
    }));

    // table data rows to show only, excluding the header row.
    const tableRows = tableData.content.slice(1, MAX_TABLE_ROWS_TO_SHOW + 1);

    return (
      <TableSortable
        columns={columns}
        data={tableRows}
        sortConfigs={{ key: '', order: 'asc' }} // optional: set default sorting
        sortIcons={{
          sortNone: <Icon kind='sortNone' size='xSmall' />,
          sortAsc: <Icon kind='sortUp' size='xSmall' />,
          sortDesc: <Icon kind='sortDown' size='xSmall' />,
        }}
      />
    );
  };

  const renderContent = () => {
    switch (kind) {
      case GeneratedContentTypeEnum.GENERATING_CONTENT:
        return (
          <section className='Ispm-GeneratedContentContainer'>
            <TopHeaderPanel isPanelLoading />
            <GeneratingContent />
          </section>
        );

      case GeneratedContentTypeEnum.CHART: {
        if (
          !('content' in data) ||
          data.contentType !== 'chart' ||
          !isValidUrl(data.content)
        ) {
          updateChatOverlayState('showGeneratedContent', false);

          Logger.info(
            'GeneratedContentContainer --> panel kind - chart  --> no content present in response',
            { data }
          );

          return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const formattedQuery = format(data.query);

        return (
          <section className='Ispm-GeneratedContentContainer'>
            <TopHeaderPanel
              headerText={data.request?.freeText ?? 'Content panel header'}
              toggleSelection={toggleSelection}
            />
            <Box className='Ispm-GeneratedContentContainer-headerContentContainer'>
              <TableHeader
                numRecords={data.content.length - 1}
                toggleSelection={toggleSelection}
                setToggleSelection={setToggleSelection}
              />
              {toggleSelection === TOGGLE_VALUES.VISUALIZATION ? (
                <div
                  data-testid='GeneratedContentContainerChart'
                  className='Ispm-GeneratedContentContainer-chart'>
                  <Iframe
                    iframeUrl={data.content}
                    title={`${l('Chart generated for your query')} ${
                      data.request?.freeText ?? ''
                    }`}
                    testId={CHATOVERLAY_DATAPANEL}
                    LoaderComponent={<GeneratingContent />}
                  />
                </div>
              ) : (
                <div
                  data-testid='GeneratedContentContainerSql'
                  className='Ispm-GeneratedContentContainer-sql'>
                  <pre className='Ispm-GeneratedContentContainer-sql-content'>
                    <code>{formattedQuery}</code>
                  </pre>
                </div>
              )}
            </Box>
          </section>
        );
      }

      case GeneratedContentTypeEnum.SQL_ERROR: {
        let formattedQuery = '';

        if ('query' in data && typeof data.query === 'string') {
          formattedQuery = format(data.query);
        }

        return (
          <section className='Ispm-GeneratedContentContainer'>
            <TopHeaderPanel
              headerText={
                'request' in data
                  ? data.request?.freeText
                  : 'Content panel header'
              }
              toggleSelection={toggleSelection}
            />
            <Box className='Ispm-GeneratedContentContainer-headerContentContainer'>
              <SqlErrorToggle
                detailedSteps={
                  'detailedSteps' in data ? data.detailedSteps : []
                }
                toggleSelection={toggleSelection}
                setToggleSelection={setToggleSelection}
                sqlQuery={formattedQuery}
              />
              {toggleSelection === TOGGLE_VALUES.VISUALIZATION ? (
                <CantBeDisplayedSqlError />
              ) : (
                <div className='Ispm-GeneratedContentContainer-sql'>
                  <pre className='Ispm-GeneratedContentContainer-sql-content'>
                    <code>{formattedQuery}</code>
                  </pre>
                </div>
              )}
            </Box>
          </section>
        );
      }

      case GeneratedContentTypeEnum.TABLE: {
        if (
          !('content' in data) ||
          data.content.length === 0 ||
          data.contentType !== 'sampleData'
        ) {
          updateChatOverlayState('showGeneratedContent', false);

          Logger.info(
            'GeneratedContentContainer --> panel kind - table  --> no content present in response',
            { data }
          );

          return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const formattedQuery = format(data.query);

        return (
          <section className='Ispm-GeneratedContentContainer'>
            <TopHeaderPanel
              headerText={
                data.request?.freeText ?? 'This is default content panel header'
              }
              numRecords={data.content.length - 1}
              toggleSelection={toggleSelection}
            />

            <Box className='Ispm-GeneratedContentContainer-headerContentContainer'>
              <TableHeader
                numRecords={Math.min(
                  data.content.length - 1,
                  MAX_TABLE_ROWS_TO_SHOW
                )}
                toggleSelection={toggleSelection}
                setToggleSelection={setToggleSelection}
              />
              {toggleSelection === TOGGLE_VALUES.VISUALIZATION ? (
                <div
                  className='Ispm-GeneratedContentContainer-table'
                  data-testid='GeneratedContentContainerTable'>
                  {generateTableContent(data)}
                </div>
              ) : (
                <div
                  className='Ispm-GeneratedContentContainer-sql'
                  data-testid='GeneratedContentContainerSql'>
                  <pre className='Ispm-GeneratedContentContainer-sql-content'>
                    <code>{formattedQuery}</code>
                  </pre>
                </div>
              )}
            </Box>
          </section>
        );
      }

      case GeneratedContentTypeEnum.LOW_CONFIDENCE: {
        // case GeneratedContentTypeEnum.HIGH_CONFIDENCE: {
        if (
          !('contentType' in data) ||
          data.contentType !== 'query' ||
          'error' in data
        ) {
          updateChatOverlayState('showGeneratedContent', false);

          Logger.info(
            'GeneratedContentContainer --> panel kind - low confidence  --> contentType is not query'
          );

          return null;
        }

        return (
          <section className='Ispm-GeneratedContentContainer'>
            <TopHeaderPanel headerText={data.request?.freeText ?? data.query} />
            <div className='Ispm-GeneratedContentContainer-table'>
              <ContentNotAccurate
                panelHeader={
                  <LowConfidenceHeader
                    toggleSelection={toggleSelection}
                    setToggleSelection={setToggleSelection}
                  />
                }
                toggleSelection={toggleSelection}
                query={data.query}
                onClick={() => {
                  handleLowConfidenceSampleDataApi(data);
                }}
              />
            </div>
          </section>
        );
      }

      default: {
        return null;
      }
    }
  };

  return (
    renderContent() ?? (
      <div className='Ispm-GeneratedContentContainer--noContent' />
    )
  );
}

export default GeneratedContentContainer;
